import { FunctionComponent } from 'react'
import { AppTable, AppButton } from '@/shared/ui'
import { MagnifyingGlassIcon, ArrowSquareOutIcon } from '@phosphor-icons/react'
import DayJS from 'react-dayjs'

import { TaskTypes } from '../../enumerations/tasks.enumeration'
import { TaskSteps, TaskStepsColors } from '../../enumerations/task-steps.enumeration'

export type Location = {
  latitude?: number
  longitude?: number
  name?: string
}

export type Task = {
  id?: string
  job_id?: string
  job_number?: string
  task_type: TaskTypes
  step_type: TaskSteps
  origin?: Location
  destination?: Location
  state: TaskSteps
  created_at?: string
}

export type ColumnName = {
  title?: string
  dataIndex?: string
}

export type Pagination = {
  total: number
  current: number
  pageSize: number
  defaultPageSize: number
  showSizeChanger: boolean
}

export type ResourceTasksTableProps = {
  selectedTaskId?: string
  tasks?: Task[]
  onClickReference?: (id?: string) => void
  onClickShowInMap?: (task?: Task) => void
  onTableChange?: (pagination?: any) => void
  assignedTasks?: boolean
  columnNames?: ColumnName[]
  copy?: (text: string) => string
  pagination?: Pagination
  loading?: boolean
  className?: string
}

export const ResourceTasksTable: FunctionComponent<ResourceTasksTableProps> = ({
  selectedTaskId,
  tasks = [],
  onClickReference = () => {},
  onClickShowInMap = () => {},
  onTableChange = () => {},
  assignedTasks = true,
  columnNames = [
    {
      title: '',
      dataIndex: 'show_in_map'
    },
    {
      title: '# Number',
      dataIndex: 'job_number'
    },
    {
      title: 'Task',
      dataIndex: 'task_type'
    },
    {
      title: 'Store',
      dataIndex: 'store'
    },
    {
      title: 'Status',
      dataIndex: 'status'
    }
  ],
  copy = () => 'Empty',
  pagination = {
    total: 20,
    current: 1,
    pageSize: 19,
    defaultPageSize: 20,
    showSizeChanger: false
  },
  loading = false,
  className = ''
}: ResourceTasksTableProps): JSX.Element => {
  const getFormattedTasks = () =>
    tasks.map((task, idx) => {
      const { job_number, job_id, origin, step_type, state, created_at = '' } = task
      const status = assignedTasks ? step_type : state

      return {
        key: idx,
        ...task,
        show_in_map: (
          <AppButton
            onClick={() => onClickShowInMap(task)}
            className='gungnir-rtt-show-in-map'
            icon={<MagnifyingGlassIcon />}
            size='sm'
          />
        ),
        job_number: (
          <span className='gungnir-rtt-reference' onClick={() => onClickReference(job_id)}>
            {job_number} <ArrowSquareOutIcon />
          </span>
        ),
        store: origin?.name || '',
        status: (
          <span
            className='gungnir-rtt-task-status'
            style={{
              backgroundColor: TaskStepsColors[status]?.bg,
              color: TaskStepsColors[status]?.text
            }}
          >
            {copy(status)}
          </span>
        ),
        created_at: <DayJS format={'DD/MM/YYYY'}>{created_at}</DayJS>
      }
    })

  return (
    <div data-testid='gungnir-rtt' className={`gungnir-rtt ${className}`}>
      <AppTable
        loading={loading}
        onChange={onTableChange}
        dataSource={getFormattedTasks()}
        columns={columnNames}
        bordered
        pagination={pagination}
        size='sm'
        rowClassName={({ id }) => (selectedTaskId && selectedTaskId === id ? 'selected-task' : '')}
      />
    </div>
  )
}
